import type { OperationMode } from "../../../../http/lock_calculator/types";
import type { NoticeSeverity } from "./notices";

/** Who produces a notice: the backend (state derived from the saved data), the form (state derived from the
 * live values), the session (what happened since the page opened) or a rule that changed a field (cascade). */
export type NoticeOrigin = "backend" | "form" | "session" | "cascade";

export type NoticeCatalogEntry = {
  origin: NoticeOrigin;
  /** Severity of the notices the frontend builds; null when the backend, or a runtime condition, decides it. */
  severity: NoticeSeverity | null;
  /** The calculation button that resolves it. "generationMode" is the one the drafts were generated with. */
  action?: OperationMode | "generationMode";
  /** While present, the automatic and semi-automatic generation cannot run: their buttons wait. */
  blocksAutomaticGeneration?: boolean;
  /** Says why a generation produced no configuration. */
  explainsEmptyGeneration?: boolean;
};

/**
 * The single list of notice codes: what each is and does. The message of each code lives in the
 * translations (`lockCalculatorModule.notices.messages`); a test keeps the two lists, and the backend's
 * codes (services/notices.go), in step. To add a notice: one entry here, one message per language.
 */
export const NOTICE_CATALOG = {
  // ── Backend: derived from the saved data (services/notices.go) ──
  topDistanceBelowCMin: { origin: "backend", severity: null },
  topDistanceBelowLock135Min: { origin: "backend", severity: null },
  serviceClassNotAllowed: { origin: "backend", severity: null },
  axisInputsMissing: { origin: "backend", severity: null },
  combinedNeedsTwoAxes: { origin: "backend", severity: null },
  edgeDistanceUnavailable: { origin: "backend", severity: null },
  generationInputsMissing: { origin: "backend", severity: null, blocksAutomaticGeneration: true },
  noCompatibleLock: {
    origin: "backend",
    severity: null,
    blocksAutomaticGeneration: true,
    explainsEmptyGeneration: true,
  },
  noCompatibleFastener: {
    origin: "backend",
    severity: null,
    blocksAutomaticGeneration: true,
    explainsEmptyGeneration: true,
  },
  noCompatibleLockstop: {
    origin: "backend",
    severity: null,
    blocksAutomaticGeneration: true,
    explainsEmptyGeneration: true,
  },

  // ── Form: derived from the live values ──
  customCoefficientsEnabled: { origin: "form", severity: "info" },
  coefficientOverrideDiffers: { origin: "form", severity: "warning" },
  topDistanceEqualsDefault: { origin: "form", severity: "info" },
  // Info while the connectors are being filled in, a warning once a result exists: decided at runtime.
  manualGenerationInputsMissing: { origin: "form", severity: null },

  // ── Session: what happened since the page opened ──
  resultStale: { origin: "session", severity: "warning", action: "generateManual" },
  reportStale: { origin: "session", severity: "warning" },
  draftsStale: { origin: "session", severity: "warning", action: "generationMode" },
  noDraftWithinLimits: { origin: "session", severity: "warning", action: "generationMode" },

  // ── Cascades: a rule changed a field because of another ──
  lateralConfigCorrected: { origin: "cascade", severity: "info" },
  continuousControlCleared: { origin: "cascade", severity: "info" },
  secondConnectorCleared: { origin: "cascade", severity: "info" },
  lockstopCleared: { origin: "cascade", severity: "info" },
  lockstopClearedByHeaderLock: { origin: "cascade", severity: "warning" },
  headerLockClearedByLockstop: { origin: "cascade", severity: "warning" },
  combinedAxisDisabled: { origin: "cascade", severity: "info" },
  upliftConfigSetByLateral: { origin: "cascade", severity: "info" },
  upliftConfigSetByLoad: { origin: "cascade", severity: "info" },
  inclinedFastenerCleared: { origin: "cascade", severity: "info" },
  inclinedFastenerNotAllowed: { origin: "cascade", severity: "info" },
  fastenerCleared: { origin: "cascade", severity: "info" },
  finishForced: { origin: "cascade", severity: "info" },
  finishSetByServiceClass: { origin: "cascade", severity: "info" },
  secondLockCleared: { origin: "cascade", severity: "info" },
  pairCountReduced: { origin: "cascade", severity: "info" },
  topDistanceFollowedDefault: { origin: "cascade", severity: "info" },
  topOffsetFollowedDefault: { origin: "cascade", severity: "info" },
  connectorsInvalidated: { origin: "cascade", severity: "info" },
} as const satisfies Record<string, NoticeCatalogEntry>;

export type NoticeCode = keyof typeof NOTICE_CATALOG;

/** The catalog entry of a code, when the code is known (a backend code the frontend does not know is not). */
export const catalogEntry = (code: string): NoticeCatalogEntry | undefined =>
  (NOTICE_CATALOG as Record<string, NoticeCatalogEntry>)[code];

/** The fixed severity of a frontend-built notice. */
export const catalogSeverity = (code: NoticeCode): NoticeSeverity => NOTICE_CATALOG[code].severity ?? "info";

export const noticeCodesOf = (origin: NoticeOrigin): NoticeCode[] =>
  (Object.keys(NOTICE_CATALOG) as NoticeCode[]).filter((code) => NOTICE_CATALOG[code].origin === origin);

/** Whether this notice holds back the automatic and semi-automatic generation. */
export const blocksAutomaticGeneration = (code: string): boolean =>
  catalogEntry(code)?.blocksAutomaticGeneration === true;

/** Whether this notice explains an empty generation. */
export const explainsEmptyGeneration = (code: string): boolean => catalogEntry(code)?.explainsEmptyGeneration === true;
